/**
 * Frontend access control for the signed-in workspace admin.
 *
 * The backend is the real authority (it enforces permissions on every API call);
 * this module mirrors the saved permissions so the UI can hide modules the admin
 * cannot use and route them to their first allowed page.
 */
import { useEffect, useState } from 'react';
import type { ApiUser, Permissions, AccessLevel } from '../api';
import { planAllowsModule } from './subscription';
import { isExternalBillingDisabled } from './platform';

const USER_KEY = 'ef_current_user_v1';
export const USER_CHANGED_EVENT = 'ef-user-changed';

/** Maps a nav/route id used in the shell to a backend permission module. */
export const NAV_MODULE: Record<string, string> = {
  master: 'dashboard',
  dashboard: 'dashboard',
  customers: 'customers',
  overdue: 'customers',
  finance: 'finance',
  loans: 'loans',
  accounts: 'finance',
  report: 'reports',
  audit: 'audit',
  erp: 'audit',
  consultancy: 'consultancy',
  expenses: 'expenses',
  employees: 'employees',
  settings: 'settings',
};

export function setCurrentUser(user: ApiUser | null): void {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(USER_CHANGED_EVENT));
}

export function getCurrentUser(): ApiUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as ApiUser) : null;
  } catch {
    return null;
  }
}

export function isAuditorUser(user: ApiUser | null = getCurrentUser()): boolean {
  return user?.deskRole === 'auditor';
}

/** Owners, desk super admins, platform super admins, and legacy (no perms) users see everything. */
export function isFullAccessUser(user: ApiUser | null = getCurrentUser()): boolean {
  if (!user) return true; // unknown (e.g. offline fallback) → don't lock the user out
  if (user.role === 'LEDGER' || user.isLedgerLogin) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  if (user.isOrgOwner) return true;
  if (user.deskRole === 'super_admin') return true;
  const perms = user.permissions || {};
  if (Object.keys(perms).length === 0) return true; // accounts created before permissions shipped
  return false;
}

export function hasModuleAccess(module: string, level: AccessLevel = 'read'): boolean {
  const user = getCurrentUser();
  if (isAuditorUser(user) && level === 'write') return false;
  if (isFullAccessUser(user)) {
    return planAllowsModule(module);
  }
  const perms: Permissions = user!.permissions || {};
  const p = perms[module] || 'none';
  const roleOk = level === 'read' ? p === 'read' || p === 'write' : p === 'write';
  if (!roleOk) {
    // Auditors and report viewers may open audit logs when they have reports read.
    if (module === 'audit' && level === 'read' && (perms.reports === 'read' || perms.reports === 'write')) {
      return planAllowsModule(module);
    }
    return false;
  }
  return planAllowsModule(module);
}

/** Settings sections every signed-in user can open (device-local appearance). */
export const PERSONAL_SETTINGS_SECTIONS = new Set(['profile', 'language', 'font', 'theme']);

/** Settings sections always available without a subscription module gate. */
const SETTINGS_CORE = new Set(['company', 'ledgers', 'financialYear', 'profile', 'language', 'font', 'theme']);

/** Desk login (Ledger 2, etc.) — org branding + day-to-day form config + personal prefs. */
const LEDGER_SETTINGS_SECTIONS = new Set([
  'company',
  'profile',
  'line',
  'banks',
  'finance',
  'financialYear',
  'language',
  'font',
  'theme',
]);

const SETTINGS_OPEN_ORDER = [
  'language',
  'font',
  'theme',
  'profile',
  'deleteAccount',
  'company',
  'line',
  'banks',
  'finance',
  'ledgers',
  'sms',
  'payment',
  'subscription',
  'owners',
  'admins',
] as const;

export function isLedgerDeskUser(user: ApiUser | null = getCurrentUser()): boolean {
  if (!user) return false;
  if (user.role === 'LEDGER') return true;
  return Boolean(user.isLedgerLogin);
}

/** Can the signed-in admin open this nav/route id? */
export function canAccessNav(navId: string): boolean {
  if (navId === 'settings') return true;
  if (navId === 'audit' || navId === 'erp') {
    const auditAllowed = hasModuleAccess('audit', 'read');
    const reportsAllowed = hasModuleAccess('reports', 'read');
    const planAllowsAudit = planAllowsModule('audit');
    return planAllowsAudit && (auditAllowed || reportsAllowed);
  }
  const module = NAV_MODULE[navId];
  if (!module) return true;
  return hasModuleAccess(module, 'read');
}

export function canAccessSettingsSection(sectionId: string): boolean {
  const user = getCurrentUser();
  if (sectionId === 'subscription' && isExternalBillingDisabled()) return false;
  if (sectionId === 'deleteAccount') {
    const user = getCurrentUser();
    if (isLedgerDeskUser(user)) return false;
    return Boolean(user?.isOrgOwner || user?.deskRole === 'super_admin');
  }
  if (PERSONAL_SETTINGS_SECTIONS.has(sectionId)) return true;
  if (isLedgerDeskUser(user)) return LEDGER_SETTINGS_SECTIONS.has(sectionId);
  // Always show SMS settings (usage + schedule) when the user can open Settings.
  // Send/enable stays gated by planAllowsItem(SMS_REMINDER_FEATURE_ID) inside the panel.
  if (sectionId === 'sms' || sectionId === 'whatsapp') {
    return hasModuleAccess('settings', 'read');
  }
  if (SETTINGS_CORE.has(sectionId)) return true;
  return hasModuleAccess('settings', 'read');
}

/** First settings tab to open for the current user (appearance first for ledger desks). */
export function getDefaultSettingsSection(): string {
  const hit = SETTINGS_OPEN_ORDER.find((id) => canAccessSettingsSection(id));
  return hit ?? 'profile';
}

/** Can the signed-in admin create/edit/delete in this nav/route id or module? */
export function canWriteModule(navIdOrModule: string): boolean {
  if (isAuditorUser()) return false;
  if (navIdOrModule === 'erp') {
    return (
      hasModuleAccess('audit', 'write') ||
      hasModuleAccess('finance', 'write') ||
      hasModuleAccess('settings', 'write')
    );
  }
  const module = NAV_MODULE[navIdOrModule] || navIdOrModule;
  return hasModuleAccess(module, 'write');
}

/**
 * React hook returning whether the current user has WRITE access to a module.
 * Re-reads when the signed-in user / permissions change so the UI stays in sync.
 * `null`/empty module means "always writable" (e.g. non-permissioned areas).
 */
export function useCanWrite(navIdOrModule?: string | null): boolean {
  const read = () => (navIdOrModule ? canWriteModule(navIdOrModule) : true);
  const [can, setCan] = useState<boolean>(read);
  useEffect(() => {
    const update = () => setCan(read());
    update();
    if (typeof window !== 'undefined') {
      window.addEventListener(USER_CHANGED_EVENT, update);
      window.addEventListener('storage', update);
      return () => {
        window.removeEventListener(USER_CHANGED_EVENT, update);
        window.removeEventListener('storage', update);
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navIdOrModule]);
  return can;
}

/**
 * Overdue waivers & settlement discounts: Owner, Admin or Manager only (mirrors the
 * backend `canApproveWaivers`). Branch desks, Staff and Auditors cannot approve.
 */
export function canApproveWaivers(user: ApiUser | null = getCurrentUser()): boolean {
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  if (isLedgerDeskUser(user)) return false;
  if (user.isOrgOwner) return true;
  const role = user.deskRole || 'admin';
  return role === 'super_admin' || role === 'admin';
}

export function useCanApproveWaivers(): boolean {
  const [can, setCan] = useState<boolean>(() => canApproveWaivers());
  useEffect(() => {
    const update = () => setCan(canApproveWaivers());
    window.addEventListener(USER_CHANGED_EVENT, update);
    return () => window.removeEventListener(USER_CHANGED_EVENT, update);
  }, []);
  return can;
}

/** First nav id (from the supplied order) the admin is allowed to open. */
export function firstAllowedNav(order: string[]): string {
  return order.find((id) => canAccessNav(id)) || 'settings';
}
